import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import FilterBar from 'components/shared/filters/FilterBar';
import FilterMultiSelect from 'components/shared/filters/FilterMultiSelect';
import DatePicker from 'components/shared/form/DatePicker';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import {
  ADDITIONAL_FILTERS,
  COMANAGEMENT_TEAM,
  createLevelOfCareDropdown,
  createPlanningDischargeToDropdown,
  EPISODE_TYPE_DROPDOWN,
  FILTER_TYPES,
  HOSPITAL_DROPDOWN,
  LOCATION_DROPDOWN,
  PAYER_DROPDOWN,
  PHYSICIAN_GROUP_DROPDOWN,
  PHYSICIAN_TEAM_DROPDOWN,
  PLAN_TYPE_DROPDOWN,
  REVIEW_STATUS_DROPDOWN,
  sortFilterDropdowns,
} from 'constants/filterConfigs';
import { LOCATION } from 'constants/filterKeysConstants';
import { SKILLED_NURSING_FACILITY } from 'constants/locationTypes';
import { useProfile } from 'context/profile';
import FilterValueArray from 'models/filterValues/FilterValueArray';
import GroupType from 'models/GroupType';
import { DischargeCalendarFilterValue } from 'models/userPreferences/DischargeCalendarFilter';
import { indexDischargeCalendar } from 'services/api/dischargeCalendar';
import { upsertDischargeCalendarFilter } from 'services/api/preferences';
import {
  DischargeCalendarFilterState,
  DischargeCalendarView,
  useDayViewState,
  useDischargeCalendarCurrentView,
  useDischargeCalendarFilters,
  useDischargeCalendarFiltersActions,
  useListViewActions,
  useListViewState,
  useMonthViewState,
  useTwoWeekViewState,
  useWeekViewState,
} from 'stores/dischargeCalendarStore';
import { FilterOption } from 'stores/types';

import useSnfLaunchDate from './hooks/useSnfLaunchDate';
import ViewModeToggle from './shared/ViewModeToggle';
import { DISCHARGE_CALENDAR_ROUTES, DISCHARGE_CALENDAR_VIEWS } from './constants';

const VIEW_OPTIONS: { value: DischargeCalendarView; label: string }[] = [
  { value: DISCHARGE_CALENDAR_VIEWS.DAY, label: 'Day' },
  { value: DISCHARGE_CALENDAR_VIEWS.WEEK, label: 'Week' },
  { value: DISCHARGE_CALENDAR_VIEWS.TWO_WEEK, label: '2 Week' },
  { value: DISCHARGE_CALENDAR_VIEWS.MONTH, label: 'Month' },
];

interface DischargeCalendarFilterBarProps {
  onApplyFilters: (filters: DischargeCalendarFilterState & { search?: string }) => void;
}

export default function DischargeCalendarFilterBar({ onApplyFilters }: DischargeCalendarFilterBarProps) {
  const { profile } = useProfile();
  const navigate = useNavigate();

  const filters = useDischargeCalendarFilters();
  const currentView = useDischargeCalendarCurrentView();
  const { setFilter, setFilters, setCurrentView, clearFilters, setSearch } = useDischargeCalendarFiltersActions();

  const dayViewState = useDayViewState();
  const weekViewState = useWeekViewState();
  const twoWeekViewState = useTwoWeekViewState();
  const monthViewState = useMonthViewState();
  const listViewState = useListViewState();
  const { setStartDate, setEndDate } = useListViewActions();
  const [selectedStartDate, setSelectedStartDate] = useState<Date>(listViewState.startDate);
  const [selectedEndDate, setSelectedEndDate] = useState<Date>(listViewState.endDate);

  const snfLaunchDate = useSnfLaunchDate();
  const isListView = currentView === DISCHARGE_CALENDAR_VIEWS.LIST;

  const currentDateRange = useMemo(() => {
    switch (currentView) {
      case DISCHARGE_CALENDAR_VIEWS.DAY:
        return dayViewState;
      case DISCHARGE_CALENDAR_VIEWS.WEEK:
        return weekViewState;
      case DISCHARGE_CALENDAR_VIEWS.TWO_WEEK:
        return twoWeekViewState;
      case DISCHARGE_CALENDAR_VIEWS.MONTH:
        return monthViewState;
      case DISCHARGE_CALENDAR_VIEWS.LIST:
      default:
        return { startDate: selectedStartDate, endDate: selectedEndDate };
    }
  }, [currentView, dayViewState, weekViewState, twoWeekViewState, monthViewState, selectedStartDate, selectedEndDate]);

  const queryClient = useQueryClient();

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['filterSlideout'], refetchType: 'none' });
  }, [queryClient, currentDateRange]);

  const { mutate: savePreferences } = useMutation({
    mutationFn: upsertDischargeCalendarFilter,
  });

  const snfGroupType = useMemo(() => {
    const snf = profile.tabProviderTypes.find((gt) => gt.apiName === SKILLED_NURSING_FACILITY);
    return snf ?? new GroupType({ apiName: SKILLED_NURSING_FACILITY, displayName: 'SNF' });
  }, [profile.tabProviderTypes]);

  const filterSections = useMemo(
    () => [
      {
        title: COMANAGEMENT_TEAM,
        filters: sortFilterDropdowns([
          { ...HOSPITAL_DROPDOWN, filterType: FILTER_TYPES.DROPDOWN },
          { ...PAYER_DROPDOWN, filterType: FILTER_TYPES.DROPDOWN },
          { ...PHYSICIAN_TEAM_DROPDOWN, filterType: FILTER_TYPES.DROPDOWN },
          { ...PHYSICIAN_GROUP_DROPDOWN, filterType: FILTER_TYPES.DROPDOWN },
          { ...LOCATION_DROPDOWN, filterType: FILTER_TYPES.DROPDOWN },
        ]),
      },
      {
        title: ADDITIONAL_FILTERS,
        filters: [
          { ...EPISODE_TYPE_DROPDOWN, filterType: FILTER_TYPES.DROPDOWN },
          { ...PLAN_TYPE_DROPDOWN, filterType: FILTER_TYPES.DROPDOWN },
          {
            ...createPlanningDischargeToDropdown(snfGroupType.id),
            filterType: FILTER_TYPES.DROPDOWN,
            placeholder: 'Planning Discharge To',
          },
          {
            ...REVIEW_STATUS_DROPDOWN,
            filterType: FILTER_TYPES.DROPDOWN,
            placeholder: 'Referral Status',
            name: 'referralStatus',
          },
          ...(profile.providedProviderTypes.length >= 2
            ? [{ ...createLevelOfCareDropdown(profile.providedProviderTypes), filterType: FILTER_TYPES.DROPDOWN }]
            : []),
        ],
      },
    ],
    [snfGroupType, profile.providedProviderTypes]
  );

  const handleLocationFilterChange = (values: FilterValueArray) => {
    setFilter(LOCATION, values);
    const updatedFilters = { ...filters, [LOCATION]: values };
    savePreferences({
      clientId: profile.actingClient.id,
      value: { ...updatedFilters, currentView } as DischargeCalendarFilterValue,
    });
  };

  const handleViewChange = (view: DischargeCalendarView) => {
    setCurrentView(view);
    const route = DISCHARGE_CALENDAR_ROUTES[view.toUpperCase() as keyof typeof DISCHARGE_CALENDAR_ROUTES];
    navigate(route);
    savePreferences({
      clientId: profile.actingClient.id,
      value: { ...filters, currentView: view } as DischargeCalendarFilterValue,
    });
  };

  const handleToggleViewMode = (toListView: boolean) => {
    if (toListView) {
      handleViewChange(DISCHARGE_CALENDAR_VIEWS.LIST);
    } else {
      // Switch back to calendar view (default to week)
      const calendarView = currentView === DISCHARGE_CALENDAR_VIEWS.LIST ? DISCHARGE_CALENDAR_VIEWS.WEEK : currentView;
      handleViewChange(calendarView);
    }
  };

  const handleApplyFilters = (updatedFilters: DischargeCalendarFilterState & { search?: string }) => {
    const { search, ...rest } = updatedFilters;
    setFilters(rest);
    setSearch(search ?? '');
    onApplyFilters(updatedFilters);
    savePreferences({
      clientId: profile.actingClient.id,
      value: { ...rest, currentView } as DischargeCalendarFilterValue,
    });
  };

  const handleClearFilters = () => {
    clearFilters();
    savePreferences({
      clientId: profile.actingClient.id,
      value: { currentView } as DischargeCalendarFilterValue,
    });
  };

  return (
    <FilterBar
      applyFilters={handleApplyFilters}
      filters={filters}
      filterSections={filterSections}
      onClearFilters={handleClearFilters}
      title='Discharge Calendar'
      resultsText='patients'
      searchPlaceholder='Search Patients'
      queryFn={(inp, sig) =>
        indexDischargeCalendar(
          { ...inp, startDate: currentDateRange.startDate, endDate: currentDateRange.endDate },
          sig
        )
      }
      quickFilterShowMinWidth={1100}
      quickFilter={
        <FilterMultiSelect
          name={LOCATION_DROPDOWN.name}
          placeholder={LOCATION_DROPDOWN.placeholder}
          getOptionLabel={LOCATION_DROPDOWN.getOptionLabel}
          getOptionValue={LOCATION_DROPDOWN.getOptionValue}
          getOptions={LOCATION_DROPDOWN.getOptions}
          onChange={handleLocationFilterChange}
          value={filters[LOCATION] as FilterOption[]}
          debounceTime={500}
        />
      }
      callToActionShowMinWidth={900} // dropdown styling gets weird between 900 and mobile
      callToAction={
        <CallToActionContainer>
          {isListView ? (
            <DatePickerContainer>
              <DatePicker
                onCalendarClose={() => setStartDate(selectedStartDate)}
                onChange={(val) => setSelectedStartDate(val as Date)}
                selected={selectedStartDate}
                minDate={snfLaunchDate ?? undefined}
                maxDate={selectedEndDate}
                clearable={false}
              />
              <Emdash>&mdash;</Emdash>
              <DatePicker
                onCalendarClose={() => setEndDate(selectedEndDate)}
                onChange={(val) => setSelectedEndDate(val as Date)}
                selected={selectedEndDate}
                minDate={snfLaunchDate && snfLaunchDate > selectedStartDate ? snfLaunchDate : selectedStartDate}
                clearable={false}
              />
            </DatePickerContainer>
          ) : (
            <div data-cy='calendarDateRange'>
              <Select
                options={VIEW_OPTIONS}
                value={VIEW_OPTIONS.find((opt) => opt.value === currentView) ?? null}
                onChange={(option) => handleViewChange(option.value)}
                getOptionValue={(opt) => opt.value}
                getOptionLabel={(opt) => opt.label}
              />
            </div>
          )}
          <ViewModeToggle isListView={isListView} onToggle={handleToggleViewMode} />
        </CallToActionContainer>
      }
    />
  );
}

const CallToActionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DatePickerContainer = styled.div`
  display: flex;
  align-items: center;

  input {
    max-width: 140px;
  }
`;

const Emdash = styled.div`
  margin: 0 8px;
`;
